import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { ComboboxField } from '@/components/sheet/field-editors'
import { useUiState } from '@/hooks/use-ui-state'
import { useRestoreFocus } from '@/hooks/use-restore-focus'
import { useIssueTemplates } from '@/hooks/use-issue-templates'
import { graphql } from '@/lib/api'
import { CREATE_ISSUE } from '@/lib/github-queries'
import { appConfig, fieldByName, keys, queryClient, useIssueFields, useRepoMeta } from '@/lib/query'
import type { IssueTemplate } from './templates'

// The body composes GitHub-form-shaped markdown (`### {label}` sections) —
// headings come from the LIVE template ymls, so intake.yml's body parse and
// this dialog can't drift apart.
function composeBody(opts: {
  template: IssueTemplate
  area: string
  client: string | null
  sections: Record<string, string>
}): string {
  const { template } = opts
  if (template.key === 'blank') {
    return opts.sections['Description']?.trim() ?? ''
  }
  const parts: string[] = []
  if (template.area) parts.push(`### ${template.area.label}\n\n${opts.area}`)
  if (template.client) {
    parts.push(`### ${template.client.label}\n\n${opts.client ?? '_No response_'}`)
  }
  for (const section of template.sections) {
    const value = opts.sections[section.label]?.trim()
    parts.push(`### ${section.label}\n\n${value || '_No response_'}`)
  }
  return parts.join('\n\n')
}

function TemplateChooser({ onPick }: { onPick: (t: IssueTemplate) => void }) {
  const { data: templates, isPending, isError } = useIssueTemplates()

  if (isPending) {
    return (
      <div className="flex flex-col gap-2 py-2">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    )
  }
  if (isError || !templates) {
    return (
      <p className="text-muted-foreground py-6 text-center text-sm">
        Couldn't load the issue templates from {appConfig.owner}/{appConfig.repo}.
      </p>
    )
  }

  return (
    <div className="-mx-6 flex flex-col">
      {templates.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onPick(t)}
          className="border-border hover:bg-hover flex items-center gap-3 border-b px-6 py-2.5 text-left last:border-b-0"
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex items-center gap-2 text-[13.5px] font-semibold">
              {t.name}
              {t.maintainersOnly && (
                <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-normal">
                  Maintainers only
                </Badge>
              )}
            </span>
            <span className="text-muted-foreground text-xs">{t.description}</span>
          </span>
          <ChevronRight className="text-faint-foreground size-4 shrink-0" />
        </button>
      ))}
    </div>
  )
}

function TemplateForm({
  template,
  onBack,
  onDone,
}: {
  template: IssueTemplate
  onBack: () => void
  onDone: (createMore: boolean) => void
}) {
  const [title, setTitle] = useState(template.titlePrefix)
  const [area, setArea] = useState('')
  const [client, setClient] = useState<string | null>(null)
  const [sections, setSections] = useState<Record<string, string>>({})
  const [createMore, setCreateMore] = useState(false)
  const [touched, setTouched] = useState(false)
  const { setSheetIssue } = useUiState()
  const fields = useIssueFields().data
  const meta = useRepoMeta().data

  const clients = fieldByName(fields, 'Client')?.options.map((o) => o.name) ?? []

  const titleValid = title.trim().length > template.titlePrefix.trim().length || (template.key === 'blank' && title.trim().length > 0)
  const areaValid = !template.area?.required || area !== ''
  const clientValid = !template.client?.required || client !== null
  const sectionsValid = template.sections.every(
    (s) => !s.required || (sections[s.label]?.trim() ?? '') !== '',
  )
  const valid = titleValid && areaValid && clientValid && sectionsValid

  const create = useMutation({
    mutationFn: async () => {
      if (!meta) throw new Error('repository metadata not loaded')
      const issueTypeId = template.issueType
        ? meta.issueTypes.find((t) => t.name === template.issueType)?.id
        : undefined
      const labelIds = template.labels
        .map((name) => meta.labels.find((l) => l.name === name)?.id)
        .filter((id): id is string => id !== undefined)
      const data = await graphql<{ createIssue: { issue: { number: number; url: string } } }>({
        query: CREATE_ISSUE,
        variables: {
          repositoryId: meta.id,
          title: title.trim(),
          body: composeBody({ template, area, client, sections }),
          issueTypeId,
          labelIds: labelIds.length ? labelIds : undefined,
        },
      })
      return data.createIssue.issue
    },
    onSuccess: (issue) => {
      queryClient.invalidateQueries({ queryKey: keys.issues })
      toast.success(`#${issue.number} created`, {
        description: 'Intake automation applies Source, area label and Stage.',
        action: { label: 'View', onClick: () => setSheetIssue(issue.number) },
      })
      onDone(createMore)
      if (createMore) {
        setTitle(template.titlePrefix)
        setSections({})
        setTouched(false)
      }
    },
    onError: (err) => {
      toast.error("Couldn't create the issue", {
        description: err instanceof Error ? err.message : undefined,
        action: { label: 'Retry', onClick: () => create.mutate() },
      })
    },
  })

  const submit = () => {
    setTouched(true)
    if (valid && !create.isPending) create.mutate()
  }

  const invalidCls = 'border-destructive'

  return (
    <div
      className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-1"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          submit()
        }
      }}
    >
      {template.intro && (
        <p className="text-muted-foreground text-xs leading-relaxed">{template.intro}</p>
      )}

      <div>
        <Label className="mb-1 text-xs font-semibold">
          Title <span className="text-destructive">*</span>
        </Label>
        <Input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={touched && !titleValid ? invalidCls : undefined}
        />
        {touched && !titleValid && (
          <p className="text-destructive mt-1 text-[11px]">A title is required</p>
        )}
      </div>

      {template.area && (
        <div>
          <Label className="mb-1 text-xs font-semibold">
            {template.area.label}{' '}
            {template.area.required && <span className="text-destructive">*</span>}
          </Label>
          <Select value={area || undefined} onValueChange={setArea}>
            <SelectTrigger size="sm" className={touched && !areaValid ? invalidCls : undefined}>
              <SelectValue placeholder="Pick an area" />
            </SelectTrigger>
            <SelectContent>
              {template.area.options.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {touched && !areaValid && (
            <p className="text-destructive mt-1 text-[11px]">Pick an area</p>
          )}
        </div>
      )}

      {template.client && (
        <div>
          <Label className="mb-1 text-xs font-semibold">
            {template.client.label}{' '}
            {template.client.required && <span className="text-destructive">*</span>}
          </Label>
          {template.client.hint && (
            <p className="text-muted-foreground mb-1 text-[11px]">{template.client.hint}</p>
          )}
          <ComboboxField modal value={client} options={clients} onChange={setClient} />
          {touched && !clientValid && (
            <p className="text-destructive mt-1 text-[11px]">Pick the client</p>
          )}
        </div>
      )}

      {template.sections.map((section) => (
        <div key={section.label}>
          <Label className="mb-1 text-xs font-semibold">
            {section.label} {section.required && <span className="text-destructive">*</span>}
          </Label>
          {section.hint && <p className="text-muted-foreground mb-1 text-[11px]">{section.hint}</p>}
          <Textarea
            rows={3}
            value={sections[section.label] ?? ''}
            placeholder={section.placeholder}
            onChange={(e) => setSections((s) => ({ ...s, [section.label]: e.target.value }))}
            className={
              touched && section.required && !(sections[section.label]?.trim() ?? '')
                ? invalidCls
                : undefined
            }
          />
        </div>
      ))}

      <div className="border-border -mx-6 mt-1 flex items-center gap-3 border-t px-6 pt-3">
        <label className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Checkbox checked={createMore} onCheckedChange={(v) => setCreateMore(v === true)} />
          Create more
        </label>
        <span className="flex-1" />
        <Button variant="outline" size="sm" onClick={onBack}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={create.isPending || (touched && !valid)}>
          Create
          <kbd className="ml-1 font-mono text-[10px] opacity-75">⌘↵</kbd>
        </Button>
      </div>
    </div>
  )
}

export function NewIssueDialog() {
  const { newIssueOpen, setNewIssueOpen } = useUiState()
  const [template, setTemplate] = useState<IssueTemplate | null>(null)
  const restoreFocus = useRestoreFocus(newIssueOpen)

  const close = () => {
    setNewIssueOpen(false)
    setTemplate(null)
  }

  return (
    <Dialog open={newIssueOpen} onOpenChange={(open) => (open ? setNewIssueOpen(true) : close())}>
      <DialogContent onCloseAutoFocus={restoreFocus} className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[13.5px]">
            {template && (
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                aria-label="Back to template chooser"
                onClick={() => setTemplate(null)}
              >
                <ArrowLeft className="size-3.5" />
              </Button>
            )}
            {template
              ? `Create new issue in ${appConfig.owner}/${appConfig.repo}: ${template.name}`
              : 'Create new issue'}
          </DialogTitle>
        </DialogHeader>
        {template ? (
          <TemplateForm
            template={template}
            onBack={close}
            onDone={(more) => {
              if (!more) close()
            }}
          />
        ) : (
          <TemplateChooser onPick={setTemplate} />
        )}
      </DialogContent>
    </Dialog>
  )
}
